import { Component, computed, effect, inject, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { delay, shuffle } from '@gametailors/web-core/misc';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidButton } from '../../components/rid-button/rid-button';
import { RidKlankCardClickable } from '../../components/rid-klank-card-clickable/rid-klank-card-clickable';
import { RidKlankCard } from '../../components/rid-klank-card/rid-klank-card';
import { RidLettermixWord } from '../../components/rid-lettermix-word/rid-lettermix-word';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidVerticalSpacer } from '../../components/rid-vertical-spacer/rid-vertical-spacer';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { IconBackspace } from '../../icons/i-backspace';
import { IconEnter } from '../../icons/i-enter';
import { PronunciationService } from '../../services/pronunciation.service';
import { AudioEffectPlayer } from '../../utils/audio.utils';
import { DebouncedCallback } from '../../utils/debounced-callback.utils';

@Component({
  selector: 'rid-lettermix',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidOverlayFeedback,
    RidTextInput,
    RidVerticalSpacer,
    RidButton,
    RidLettermixWord,
    RidBottomButton,
    RidKlankCardClickable,
    RidKlankCard,
    IconBackspace,
    IconEnter,
    RidSfxDirective,
  ],
  templateUrl: './rid-lettermix.html',
  host: { class: 'block' },
})
export class RidLettermix implements OnInit, OnDestroy {
  private readonly $pronunciationService = inject(PronunciationService);

  wordMatrix = input.required<string[][][]>();

  index = signal(0);
  progress = signal(0);
  text = signal<string[]>([]);
  assignment = signal<{
    id: string;
    options: string[];
    answers: { word: string[]; appearance: 'REVEAL' | 'CORRECT' | 'SPOILER' }[];
  }>({ id: crypto.randomUUID(), options: [], answers: [] });
  canGoToNext = computed(() => this.assignment().answers.every((a) => a.appearance !== 'SPOILER'));
  seenWords = computed(() =>
    this.assignment()
      .answers.filter((a) => a.appearance === 'CORRECT')
      .map((a) => a.word.join('')),
  );
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  revealStage = signal<'OPTIONS' | 'ANSWERS' | 'INPUT'>('OPTIONS');
  canRevealAnswers = signal(false);
  private readonly revealAnswersHint = new DebouncedCallback(() => this.canRevealAnswers.set(true));

  finished = output<void>();

  constructor() {
    effect(() => {
      if (this.canRevealAnswers()) {
        AudioEffectPlayer.play('HINT');
      }
    });
  }

  ngOnInit(): void {
    this.initialize(this.wordMatrix()[this.index()]);
  }

  ngOnDestroy(): void {
    this.revealAnswersHint.cancel();
  }

  initialize(words: string[][]) {
    let options = shuffle(Array.from(new Set(words.flat())));
    while (words.map((w) => w.join('')).includes(options.join(''))) {
      options = shuffle(options);
    }

    this.assignment.set({ id: crypto.randomUUID(), options, answers: words.map((w) => ({ word: w, appearance: 'SPOILER' })) });

    this.revealStage.set('OPTIONS');
    this.canRevealAnswers.set(false);
    this.revealAnswersHint.activate();
  }

  revealAnswers() {
    this.revealStage.set('ANSWERS');
  }

  revealInput() {
    this.revealStage.set('INPUT');
  }

  append(klank: string) {
    this.text.update((current) => [...current, klank]);
    this.revealAnswersHint.activate();
  }

  pop() {
    this.text.update((current) => current.slice(0, -1));
    this.revealAnswersHint.activate();
  }

  async check() {
    const input = this.text().join('');
    const correctWordIndex = this.wordMatrix()[this.index()].findIndex((w) => w.join('') === input);
    if (correctWordIndex >= 0) {
      this.assignment.update((assignment) => {
        const newAssignment = { ...assignment };
        newAssignment.answers[correctWordIndex].appearance = 'CORRECT';
        return newAssignment;
      });
      this.text.set([]);
      AudioEffectPlayer.play('CORRECT');
      this.overlayFeedback().showCorrect();
      await delay(250);
      await this.$pronunciationService.tryPronounce(input);
    } else {
      this.text.set([]);
      AudioEffectPlayer.play('INCORRECT');
      this.overlayFeedback().showIncorrect();
    }

    this.revealAnswersHint.activate();
  }

  next() {
    const nextIndex = this.index() + 1;
    if (nextIndex >= this.wordMatrix().length) {
      this.finish();
    } else {
      this.index.set(nextIndex);
      this.text.set([]);
      this.initialize(this.wordMatrix()[nextIndex]);
      this.progress.update((p) => p + 1);
    }
  }

  reveal() {
    this.text.set([]);
    this.assignment.update((assignment) => {
      const newAssignment = { ...assignment };
      newAssignment.answers = newAssignment.answers.map((a) => ({
        word: a.word,
        appearance: a.appearance === 'SPOILER' ? 'REVEAL' : a.appearance,
      }));
      return newAssignment;
    });

    this.revealAnswersHint.cancel();
  }

  finish() {
    this.revealAnswersHint.cancel();
    this.finished.emit();
  }
}
